// Keeps the record-page side panel (and its collapsed rail) pinned under the title band while the
// page body scrolls, on a light-grey full-height container with one edge chip and a width
// separator. The offsets ride CSS variables so the flyout can re-aim them without touching this
// file — `RouteOffCanvas.module.scss` matches the root by `[class*='collapsibleSidebar']`, so the
// root class keeps that name. No z-index — the separator and the chip are positioned after the
// panel, so page order paints them above it, and the whole sidebar stays below the title band's
// z-index: 4.
//
// COUPLING: 130 / 150 / 120 = the 60px fixed navbar + `#main`'s vertical padding (10px,
// 20px, 5px each side at the same breakpoints, `apps/panelpunches/admin/src/assets/css/style.css:14-34`)
// + `.fixedHeaderTop`'s `min-height: 50px` (`libs/webui/page/src/WebPage/FixedPage/FixedPage.module.scss:13`);
// the flyout's values live in `libs/webui/util/src/RouteOffCanvas/RouteOffCanvas.module.scss`, and
// `libs/chat/webui/src/Inbox/ConversationDetail.module.scss` reads the height.
.collapsibleSidebar {
  --collapsible-sidebar-top: 0px;
  --collapsible-sidebar-visible-height: calc(100vh - 130px);

  @media (max-width: 1199px) {
    --collapsible-sidebar-visible-height: calc(100vh - 150px);
  }

  @media (max-width: 768px) {
    --collapsible-sidebar-visible-height: calc(100vh - 120px);
  }
}

// `position-sticky` arrives via utilities in the component — only the offset lives here.
.sidePanel {
  top: var(--collapsible-sidebar-top);
}

// The sidebar container: the light-grey surface (`bg-body-tertiary`) the panel's section cards sit
// on, edge to edge — no padding here. Always the full visible height, never sized to its content;
// the cards bring their own body background, and a taller stack scrolls inside it.
.panelScroller {
  height: var(--collapsible-sidebar-visible-height);
}

// The collapsed rail on the same light grey, full visible height like the container so the two
// states match. A click anywhere on it expands the panel (the pointer cursor says so); the toggle
// chip is its only keyboard control. The label takes the link colour while the pointer is over it,
// the foreground token for an accent (styling guide §3).
.sideRail {
  height: var(--collapsible-sidebar-visible-height);
  cursor: pointer;

  &:hover .railLabel {
    color: var(--bs-link-color);
  }
}

.railLabel {
  writing-mode: vertical-rl;
  text-orientation: mixed;
}

// The separator fills the gap between the panel and the main content; a thin line shows only
// on hover and while dragging, and the focus ring shows for the keyboard.
.panelSeparator {
  cursor: col-resize;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    width: 2px;
    transform: translateX(-50%);
    transition: background-color 0.15s ease-in-out;
  }

  &:hover::after,
  &.resizing::after {
    background-color: var(--bs-link-color);
  }

  &:focus-visible {
    outline: 2px solid var(--bs-link-color);
    outline-offset: 2px;
  }
}
